/**
 * FikoCMMMobileComponent - Mobil Tarama Sayfası
 *
 * QR kod ile eşleştirme sonrası telefonda açılan sayfa.
 * Kamera erişimi, WebRTC stream ve tarama kontrolleri.
 */

import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { io, Socket } from 'socket.io-client';

type MobileState = 'connecting' | 'paired' | 'streaming' | 'error' | 'disconnected';

@Component({
    selector: 'fikoyt-cmm-mobile',
    standalone: true,
    imports: [CommonModule],
    template: `
<div class="cmm-mobile">
    <!-- Header -->
    <header class="header">
        <div class="logo">F</div>
        <h1>Canlı Tarama</h1>
        <div class="status">
            <span class="status-dot" [class]="state"></span>
            <span class="status-text">{{ statusText }}</span>
        </div>
    </header>

    <!-- Bağlantı Ekranı -->
    @if (state === 'connecting' || state === 'error' || state === 'disconnected') {
    <div class="connect-screen">
        @if (state === 'connecting') {
        <div class="loader"></div>
        <p>PC'ye bağlanılıyor...</p>
        }

        @if (state === 'error' || state === 'disconnected') {
        <div class="error-icon">!</div>
        <p class="error-message">{{ errorMessage }}</p>
        <button class="btn primary" (click)="reconnect()">Tekrar Dene</button>
        }
    </div>
    }

    <!-- Kamera Ekranı -->
    @if (state === 'paired' || state === 'streaming') {
    <div class="camera-screen">
        <div class="video-container">
            <video #videoElement autoplay playsinline muted></video>

            <!-- Overlay -->
            <div class="overlay">
                <div class="top-bar">
                    <span class="badge" [class.recording]="isCapturing">
                        {{ isCapturing ? 'REC' : 'HAZIR' }}
                    </span>
                    <span class="badge">{{ fps }} FPS</span>
                </div>

                <!-- Tarama Rehberi -->
                @if (isCapturing) {
                <div class="scan-guide" [class.warning]="statusText.includes('⚠️')" [class.success]="statusText.includes('✅')">
                    <div class="guide-icon">📱↔️</div>
                    <div class="guide-text">{{ statusText }}</div>
                    <div class="guide-subtext">Objenin etrafında yavaşça dönerek tarayın</div>
                </div>
                }
            </div>
        </div>

        <!-- İstatistikler -->
        <div class="stats">
            <div class="stat">
                <span class="value">{{ frameCount }}</span>
                <span class="label">Kare</span>
            </div>
            <div class="stat">
                <span class="value">{{ duration }}s</span>
                <span class="label">Süre</span>
            </div>
            <div class="stat">
                <span class="value">{{ quality }}</span>
                <span class="label">Kalite</span>
            </div>
        </div>

        <!-- Kontroller -->
        <div class="controls">
            <button class="control-btn" [class.active]="flashOn" (click)="toggleFlash()">
                <i class="icon">🔦</i>
            </button>
            <button class="control-btn primary" [class.active]="isCapturing" (click)="toggleCapture()">
                <i class="icon">{{ isCapturing ? '⏹' : '⏺' }}</i>
            </button>
            <button class="control-btn" (click)="switchCamera()">
                <i class="icon">🔄</i>
            </button>
        </div>
    </div>
    }
</div>
    `,
    styles: [`
        :host {
            display: block;
            min-height: 100vh;
            background: #1a1a2e;
            color: #fff;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            overflow-y: auto;
        }

        .cmm-mobile {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            padding-bottom: 20px;
        }

        .header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background: linear-gradient(135deg, #16213e 0%, #0f3460 100%);
        }

        .logo {
            width: 32px;
            height: 32px;
            background: #00E5FF;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: #1a1a2e;
        }

        .header h1 {
            font-size: 16px;
            font-weight: 600;
            margin: 0;
        }

        .status {
            margin-left: auto;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .status-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #666;
        }

        .status-dot.connecting { background: #FFD700; animation: pulse 1s infinite; }
        .status-dot.paired, .status-dot.streaming { background: #00FF00; }
        .status-dot.error, .status-dot.disconnected { background: #FF4444; }

        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }

        .status-text {
            font-size: 12px;
            color: #888;
        }

        /* Connect Screen */
        .connect-screen {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 20px;
            padding: 20px;
        }

        .loader {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(0, 229, 255, 0.2);
            border-top-color: #00E5FF;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            to { transform: rotate(360deg); }
        }

        .error-icon {
            width: 60px;
            height: 60px;
            background: #FF4444;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 32px;
            font-weight: bold;
        }

        .error-message {
            color: #FF4444;
            text-align: center;
            max-width: 280px;
        }

        .btn {
            padding: 14px 32px;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
        }

        .btn.primary {
            background: #00E5FF;
            color: #1a1a2e;
        }

        /* Camera Screen */
        .camera-screen {
            display: flex;
            flex-direction: column;
            padding: 16px;
        }

        .video-container {
            height: 50vh;
            max-height: 400px;
            background: #000;
            border-radius: 12px;
            overflow: hidden;
            position: relative;
        }

        video {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
        }

        .top-bar {
            display: flex;
            justify-content: space-between;
            padding: 12px;
        }

        .badge {
            background: rgba(0, 0, 0, 0.6);
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 12px;
        }

        .badge.recording {
            background: rgba(255, 0, 0, 0.7);
        }

        /* Tarama Rehberi */
        .scan-guide {
            position: absolute;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.8);
            padding: 12px 20px;
            border-radius: 12px;
            text-align: center;
            border: 1px solid rgba(0, 229, 255, 0.5);
            animation: pulse-guide 2s ease-in-out infinite;
        }

        @keyframes pulse-guide {
            0%, 100% { opacity: 0.9; transform: translateX(-50%) scale(1); }
            50% { opacity: 1; transform: translateX(-50%) scale(1.02); }
        }

        .guide-icon {
            font-size: 24px;
            margin-bottom: 6px;
        }

        .guide-text {
            font-size: 13px;
            font-weight: 600;
            color: #00E5FF;
        }

        .guide-subtext {
            font-size: 11px;
            color: #888;
            margin-top: 4px;
        }

        .scan-guide.warning {
            border-color: rgba(255, 193, 7, 0.8);
            background: rgba(255, 193, 7, 0.15);
        }

        .scan-guide.warning .guide-text {
            color: #FFC107;
        }

        .scan-guide.success {
            border-color: rgba(76, 175, 80, 0.8);
            background: rgba(76, 175, 80, 0.15);
        }

        .scan-guide.success .guide-text {
            color: #4CAF50;
        }

        .stats {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            margin: 12px 0;
        }

        .stat {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 8px;
            padding: 8px;
            text-align: center;
        }

        .stat .value {
            display: block;
            font-size: 18px;
            font-weight: bold;
            color: #00E5FF;
        }

        .stat .label {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
        }

        .controls {
            display: flex;
            justify-content: center;
            gap: 16px;
            padding: 16px 0;
        }

        .control-btn {
            width: 56px;
            height: 56px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.3);
            color: #fff;
            font-size: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
        }

        .control-btn.primary {
            width: 72px;
            height: 72px;
            background: #00E5FF;
            border-color: #00E5FF;
            color: #1a1a2e;
        }

        .control-btn.active {
            background: #FFD700;
            border-color: #FFD700;
            color: #1a1a2e;
        }

        .icon {
            font-style: normal;
        }
    `]
})
export class FikoCMMMobileComponent implements OnInit, OnDestroy {
    // State
    state: MobileState = 'connecting';
    statusText = 'Bağlanıyor...';
    errorMessage = '';

    // Session
    sessionId = '';
    pairingCode = '';

    // Camera
    localStream: MediaStream | null = null;
    facingMode: 'environment' | 'user' = 'environment';
    flashOn = false;

    // Stats
    isCapturing = false;
    frameCount = 0;
    duration = 0;
    fps = 0;
    quality = 'HD';

    // Socket & WebRTC
    private socket: Socket | null = null;
    private peerConnection: RTCPeerConnection | null = null;
    private videoElement: HTMLVideoElement | null = null;

    private captureStartTime = 0;
    private frameCountStart = 0;
    private statsInterval: any = null;

    // IMU Sensör verileri
    private imuData = {
        alpha: 0, beta: 0, gamma: 0,
        accelerationX: 0, accelerationY: 0, accelerationZ: 0,
        rotationRateAlpha: 0, rotationRateBeta: 0, rotationRateGamma: 0
    };
    private imuInterval: any = null;
    private orientationHandler: ((e: DeviceOrientationEvent) => void) | null = null;
    private motionHandler: ((e: DeviceMotionEvent) => void) | null = null;

    constructor(
        private route: ActivatedRoute,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        // URL parametrelerini al
        this.route.queryParams.subscribe(params => {
            this.sessionId = params['session'] || '';
            this.pairingCode = params['code'] || '';

            if (this.sessionId && this.pairingCode) {
                this.connect();
            } else {
                this.state = 'error';
                this.errorMessage = 'Geçersiz bağlantı linki. QR kodu tekrar taratın.';
            }
        });
    }

    ngOnDestroy(): void {
        this.cleanup();
    }

    private connect(): void {
        this.state = 'connecting';
        this.statusText = 'Bağlanıyor...';

        // Sunucu URL'ini belirle
        const serverUrl = this.detectServerUrl();
        console.log('[CMM Mobile] Sunucu URL:', serverUrl);
        console.log('[CMM Mobile] Session:', this.sessionId, 'Code:', this.pairingCode);

        this.socket = io(serverUrl, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            timeout: 10000
        });

        this.socket.on('connect', () => {
            console.log('[CMM Mobile] Socket bağlandı, session katılıyor...');
            this.statusText = 'Oturuma katılıyor...';
            this.cd.detectChanges();

            // Session'a katıl
            this.socket?.emit('FikoYT.cmm.mobile.join', {
                sessionId: this.sessionId,
                pairingCode: this.pairingCode,
                deviceName: this.getDeviceName()
            });
        });

        this.socket.on('connect_error', (error) => {
            console.error('[CMM Mobile] Bağlantı hatası:', error);
            this.state = 'error';
            this.errorMessage = `Sunucuya bağlanılamadı (${serverUrl}). Go sunucusu çalışıyor mu?`;
            this.cd.detectChanges();
        });

        this.socket.on('FikoYT.cmm.mobile.joined', (data) => {
            console.log('[CMM Mobile] Oturuma katıldı:', data);
            this.state = 'paired';
            this.statusText = 'Eşleşti';
            this.startCamera();
            this.cd.detectChanges();
        });

        this.socket.on('FikoYT.cmm.mobile.error', (data) => {
            console.error('[CMM Mobile] Hata:', data);
            this.state = 'error';
            this.errorMessage = data.error || 'Bağlantı hatası';
            this.cd.detectChanges();
        });

        // WebRTC
        this.socket.on('FikoYT.cmm.webrtc.answer', async (data) => {
            if (data.from === 'pc' && data.answer) {
                await this.handleAnswer(data.answer);
            }
        });

        this.socket.on('FikoYT.cmm.webrtc.ice', async (data) => {
            if (data.from === 'pc' && data.candidate) {
                await this.handleIceCandidate(data.candidate);
            }
        });

        this.socket.on('FikoYT.cmm.camera.control', (data) => {
            this.handleCameraControl(data);
        });

        this.socket.on('FikoYT.cmm.disconnected', () => {
            this.state = 'disconnected';
            this.errorMessage = 'PC bağlantısı kesildi.';
            this.cd.detectChanges();
        });

        // PC'den gelen durum değişiklikleri
        this.socket.on('FikoYT.cmm.state.changed', (data) => {
            console.log('[CMM Mobile] PC durumu değişti:', data);
            if (data.state === 'connected') {
                this.state = 'streaming';
                this.statusText = 'Aktarım Aktif';
            }
            this.cd.detectChanges();
        });

        // PC'den gelen tarama durumu
        this.socket.on('FikoYT.cmm.scan.status', (data) => {
            console.log('[CMM Mobile] Tarama durumu:', data);
            if (data.pointCount !== undefined) {
                this.frameCount = data.pointCount;
            }
            if (data.status) {
                this.statusText = data.status;
            }
            this.cd.detectChanges();
        });

        this.socket.on('disconnect', () => {
            if (this.state !== 'disconnected') {
                this.state = 'error';
                this.statusText = 'Bağlantı kesildi';
                this.cd.detectChanges();
            }
        });
    }

    private async startCamera(): Promise<void> {
        try {
            const constraints = {
                video: {
                    facingMode: this.facingMode,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    frameRate: { ideal: 30 }
                },
                audio: false
            };

            this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

            // Video element'i bul
            setTimeout(() => {
                this.videoElement = document.querySelector('video');
                if (this.videoElement && this.localStream) {
                    this.videoElement.srcObject = this.localStream;

                    this.videoElement.onloadedmetadata = () => {
                        this.sendCameraInfo();
                        this.initWebRTC();
                    };
                }
            }, 100);

            // Kalite belirle
            const track = this.localStream.getVideoTracks()[0];
            const settings = track.getSettings();
            this.quality = (settings.height || 0) >= 1080 ? 'FHD' : ((settings.height || 0) >= 720 ? 'HD' : 'SD');

        } catch (error) {
            console.error('[CMM Mobile] Kamera hatası:', error);
            this.state = 'error';
            this.errorMessage = 'Kamera erişimi reddedildi.';
            this.cd.detectChanges();
        }
    }

    private sendCameraInfo(): void {
        if (!this.localStream) return;

        const track = this.localStream.getVideoTracks()[0];
        const settings = track.getSettings();

        this.socket?.emit('FikoYT.cmm.camera.info', {
            sessionId: this.sessionId,
            info: {
                deviceId: settings.deviceId || '',
                deviceName: this.getDeviceName(),
                resolution: {
                    width: settings.width || 0,
                    height: settings.height || 0
                },
                fps: settings.frameRate || 30
            }
        });
    }

    private async initWebRTC(): Promise<void> {
        const iceServers = [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' }
        ];

        this.peerConnection = new RTCPeerConnection({ iceServers });

        // Track ekle
        this.localStream?.getTracks().forEach(track => {
            this.peerConnection?.addTrack(track, this.localStream!);
        });

        // ICE candidate
        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                this.socket?.emit('FikoYT.cmm.webrtc.ice', {
                    sessionId: this.sessionId,
                    candidate: event.candidate,
                    from: 'mobile'
                });
            }
        };

        // Connection state
        this.peerConnection.onconnectionstatechange = () => {
            console.log('[CMM Mobile] WebRTC state:', this.peerConnection?.connectionState);

            if (this.peerConnection?.connectionState === 'connected') {
                this.state = 'streaming';
                this.statusText = 'Aktarım Aktif';

                this.socket?.emit('FikoYT.cmm.webrtc.connected', {
                    sessionId: this.sessionId
                });

                this.startStatsUpdate();
                this.cd.detectChanges();
            }
        };

        // Offer oluştur
        const offer = await this.peerConnection.createOffer();
        await this.peerConnection.setLocalDescription(offer);

        this.socket?.emit('FikoYT.cmm.webrtc.offer', {
            sessionId: this.sessionId,
            offer: offer,
            from: 'mobile'
        });
    }

    private async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
        try {
            await this.peerConnection?.setRemoteDescription(new RTCSessionDescription(answer));
        } catch (error) {
            console.error('[CMM Mobile] Answer hatası:', error);
        }
    }

    private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
        try {
            await this.peerConnection?.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (error) {
            console.error('[CMM Mobile] ICE hatası:', error);
        }
    }

    private handleCameraControl(data: any): void {
        const track = this.localStream?.getVideoTracks()[0];
        if (!track) return;

        switch (data.command) {
            case 'torch':
                this.setFlash(data.params?.enabled);
                break;
        }
    }

    private startStatsUpdate(): void {
        this.statsInterval = setInterval(() => {
            if (this.isCapturing) {
                this.frameCount++;
                this.duration = Math.floor((Date.now() - this.captureStartTime) / 1000);

                const elapsed = (Date.now() - this.frameCountStart) / 1000;
                if (elapsed > 0) {
                    this.fps = Math.round(this.frameCount / elapsed);
                }
            }
            this.cd.detectChanges();
        }, 100);
    }

    toggleFlash(): void {
        this.flashOn = !this.flashOn;
        this.setFlash(this.flashOn);
    }

    private setFlash(enabled: boolean): void {
        const track = this.localStream?.getVideoTracks()[0];
        if (!track) return;

        const capabilities = track.getCapabilities() as any;
        if (capabilities.torch) {
            track.applyConstraints({
                advanced: [{ torch: enabled } as any]
            });
            this.flashOn = enabled;
        }
    }

    async switchCamera(): Promise<void> {
        this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';

        // Mevcut stream'i durdur
        this.localStream?.getTracks().forEach(track => track.stop());

        // Yeni kamerayı başlat
        await this.startCamera();
    }

    // ========== IMU SENSÖR YÖNETİMİ ==========

    /**
     * IMU sensörlerini başlat (DeviceOrientation + DeviceMotion)
     */
    private async startIMUSensors(): Promise<void> {
        try {
            // iOS 13+ için izin iste
            if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
                const permission = await (DeviceOrientationEvent as any).requestPermission();
                if (permission !== 'granted') {
                    console.warn('[CMM Mobile] DeviceOrientation izni reddedildi');
                }
            }

            if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
                const permission = await (DeviceMotionEvent as any).requestPermission();
                if (permission !== 'granted') {
                    console.warn('[CMM Mobile] DeviceMotion izni reddedildi');
                }
            }

            // DeviceOrientation event listener
            this.orientationHandler = (event: DeviceOrientationEvent) => {
                this.imuData.alpha = event.alpha || 0;  // Z-axis (compass)
                this.imuData.beta = event.beta || 0;    // X-axis (front-back tilt)
                this.imuData.gamma = event.gamma || 0;  // Y-axis (left-right tilt)
            };
            window.addEventListener('deviceorientation', this.orientationHandler);

            // DeviceMotion event listener
            this.motionHandler = (event: DeviceMotionEvent) => {
                const acc = event.accelerationIncludingGravity;
                const rot = event.rotationRate;

                if (acc) {
                    this.imuData.accelerationX = acc.x || 0;
                    this.imuData.accelerationY = acc.y || 0;
                    this.imuData.accelerationZ = acc.z || 0;
                }

                if (rot) {
                    this.imuData.rotationRateAlpha = rot.alpha || 0;
                    this.imuData.rotationRateBeta = rot.beta || 0;
                    this.imuData.rotationRateGamma = rot.gamma || 0;
                }
            };
            window.addEventListener('devicemotion', this.motionHandler);

            // IMU verilerini PC'ye gönder (30 FPS)
            this.imuInterval = setInterval(() => {
                if (this.isCapturing && this.socket) {
                    this.socket.emit('FikoYT.cmm.imu.data', {
                        sessionId: this.sessionId,
                        imu: {
                            ...this.imuData,
                            timestamp: Date.now()
                        }
                    });
                }
            }, 33); // ~30 FPS

            console.log('[CMM Mobile] IMU sensörleri başlatıldı');

        } catch (error) {
            console.error('[CMM Mobile] IMU sensör hatası:', error);
        }
    }

    /**
     * IMU sensörlerini durdur
     */
    private stopIMUSensors(): void {
        if (this.orientationHandler) {
            window.removeEventListener('deviceorientation', this.orientationHandler);
            this.orientationHandler = null;
        }

        if (this.motionHandler) {
            window.removeEventListener('devicemotion', this.motionHandler);
            this.motionHandler = null;
        }

        if (this.imuInterval) {
            clearInterval(this.imuInterval);
            this.imuInterval = null;
        }

        console.log('[CMM Mobile] IMU sensörleri durduruldu');
    }

    toggleCapture(): void {
        this.isCapturing = !this.isCapturing;

        if (this.isCapturing) {
            this.captureStartTime = Date.now();
            this.frameCountStart = Date.now();
            this.frameCount = 0;

            // IMU sensörlerini başlat
            this.startIMUSensors();

            // PC'ye tarama başlat komutu gönder
            this.socket?.emit('FikoYT.cmm.scan.control', {
                sessionId: this.sessionId,
                command: 'start',
                name: `Telefon Taraması ${new Date().toLocaleTimeString('tr-TR')}`
            });
            console.log('[CMM Mobile] PC\'ye tarama başlat komutu gönderildi');
        } else {
            // IMU sensörlerini durdur
            this.stopIMUSensors();

            // PC'ye taramayı tamamla komutu gönder
            this.socket?.emit('FikoYT.cmm.scan.control', {
                sessionId: this.sessionId,
                command: 'complete'
            });
            console.log('[CMM Mobile] PC\'ye tarama tamamla komutu gönderildi');
        }
    }

    reconnect(): void {
        this.cleanup();
        this.connect();
    }

    private cleanup(): void {
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
        }

        // IMU sensörlerini durdur
        this.stopIMUSensors();

        this.localStream?.getTracks().forEach(track => track.stop());
        this.peerConnection?.close();
        this.socket?.disconnect();

        this.localStream = null;
        this.peerConnection = null;
        this.socket = null;
    }

    private detectServerUrl(): string {
        const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
        const host = window.location.hostname;

        // Go sunucu portu her zaman 9000
        const GO_SERVER_PORT = '9000';

        return `${protocol}//${host}:${GO_SERVER_PORT}`;
    }

    private getDeviceName(): string {
        const ua = navigator.userAgent;
        if (/iPhone/.test(ua)) return 'iPhone';
        if (/iPad/.test(ua)) return 'iPad';
        if (/Android/.test(ua)) {
            const match = ua.match(/Android[^;]*;\s*([^)]+)/);
            return match ? match[1].trim() : 'Android';
        }
        return 'Mobil Cihaz';
    }
}
